/**
 * Shared helpers for the learner ("user") store modules (Zustand).
 *
 * Auth is cookie-based: the access token is held in memory by `authSession`
 * and the refresh token lives in an httpOnly cookie.
 */
import { API_URL } from "../../constants";
import { toDisplayString } from "../../utils/toDisplayString";
import authSession from "../../lib/authSession";

export { API_URL };

/** Standard JSON headers; attaches the bearer token when present. */
export function authHeaders(token) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** Resolve the current access token from the store, then the persisted session. */
export function resolveToken(get) {
  const state = get();
  return state.token || authSession.getToken() || "";
}

/**
 * Turn an API error payload + HTTP status into a human-readable message.
 * Handles `{ error: { message } }`, `{ error: "…" }`, `{ message: "…" }`,
 * validation maps (`{ email: ["taken"] }`) and status-only fallbacks.
 */
export function extractApiError(data, status) {
  const message = toDisplayString(data?.error ?? data?.message ?? data?.errors, "");
  if (message && message !== "{}") return message;

  if (status === 400 || status === 422) {
    return "Some of the details are invalid. Please check and try again.";
  }
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "We couldn't find what you were looking for.";
  if (status === 409) return "That already exists. Please try something else.";
  if (status === 429) return "Too many requests. Please slow down and try again.";
  if (status >= 500) return "Something went wrong on our end. Please try again.";
  return `Request failed (${status})`;
}

/** Authenticated JSON request that unwraps the `{ error: { message } }` envelope. */
export async function apiRequest(get, path, { method = "GET", body, token, credentials } = {}) {
  const bearer = token !== undefined ? token : resolveToken(get);

  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      // Auth endpoints pass credentials: "include"; data endpoints authenticate
      // with the Bearer token (the API calls default to credentials: "omit").
      ...(credentials ? { credentials } : {}),
      headers: authHeaders(bearer),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    // Network/CORS failure — fetch rejects before any response exists.
    throw new Error("Unable to reach the server. Please check your connection and try again.");
  }

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const looksJson =
      contentType.includes("application/json") ||
      text.trim().startsWith("{") ||
      text.trim().startsWith("[");
    if (looksJson) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    throw new Error(extractApiError(data, response.status));
  }

  return data;
}

/** Normalize list shapes into `{ items, total, totalPages }`. */
export function normalizeList(data, keys = []) {
  if (!data || typeof data !== "object") return { items: [], total: 0, totalPages: 1 };

  let items = [];
  if (Array.isArray(data)) {
    items = data;
  } else if (Array.isArray(data.data)) {
    items = data.data;
  } else {
    const payload = data.data && typeof data.data === "object" ? data.data : data;
    items = payload.items || data.items || [];
    if (!items.length) {
      for (const key of keys) {
        if (Array.isArray(payload[key])) {
          items = payload[key];
          break;
        }
        if (Array.isArray(data[key])) {
          items = data[key];
          break;
        }
      }
    }
  }

  const pagination = data.pagination || {};
  return {
    items: Array.isArray(items) ? items : [],
    total: pagination.total ?? data.total ?? (Array.isArray(items) ? items.length : 0),
    totalPages:
      pagination.totalPages ?? pagination.pages ?? data.totalPages ?? data.pages ?? 1,
  };
}

/** Recursively find a token string in a login/refresh response. */
export function findToken(node, visited = new Set()) {
  if (node === null || typeof node !== "object" || visited.has(node)) return undefined;
  visited.add(node);
  for (const key of ["token", "accessToken", "access_token", "authToken", "jwt"]) {
    if (typeof node[key] === "string" && node[key].length > 0) return node[key];
  }
  for (const key of ["data", "result", "user", "auth", "payload"]) {
    const nested = node[key];
    if (nested && typeof nested === "object") {
      const found = findToken(nested, visited);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

/** Recursively find a refresh token string in a login/refresh response. */
export function findRefreshToken(node, visited = new Set()) {
  if (node === null || typeof node !== "object" || visited.has(node)) return undefined;
  visited.add(node);
  for (const key of ["refreshToken", "refresh_token"]) {
    if (typeof node[key] === "string" && node[key].length > 0) return node[key];
  }
  for (const key of ["data", "result", "user", "auth", "payload"]) {
    const nested = node[key];
    if (nested && typeof nested === "object") {
      const found = findRefreshToken(nested, visited);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

/**
 * Authenticated file download. Saves the response as a file, or follows a JSON
 * `{ url }` payload when an endpoint returns a signed URL instead of bytes.
 * Used by the learner library book/journal download endpoints.
 */
export async function downloadFile(get, path, filename) {
  const response = await fetch(`${API_URL}${path}`, {
    headers: authHeaders(resolveToken(get)),
  });

  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const data = await response.json();
    const url = data?.url || data?.downloadUrl || data?.data?.url || data?.data?.downloadUrl;
    if (url) {
      window.open(url, "_blank", "noopener");
      return { url };
    }
    throw new Error("Download link unavailable");
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename || "download";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
  return {};
}

